import Decimal from 'decimal.js'
import { thousandsFormat } from '/@/components/BaseChart/format'

/**
 * bigNumber 相乘
 * @param x
 * @param y
 */
export const multiplied = (x: number | string | Decimal, y: number | string | Decimal) => {
  const result = Decimal.mul(x, y)
  return result.isNaN() ? 0 : result.toNumber()
}

/**
 * 安全相乘， 如果不能做乘法将会返回原始值
 * @param x
 * @param y
 */
export const safeMultiplied = (x: number | string | Decimal, y: number | string | Decimal) => {
  if (x === null ) {
    return x
  }

  try {
    const result = Decimal.mul(x, y)
    return result.isNaN() ? x : result.toNumber()
  } catch (error) {
    return x
  }
}

/**
 * 转换纯数字
 * @param numStr
 */
export const toPureNum = (numStr: string) => {
  return Number(
    numStr
      .toString()
      .split('')
      .filter(item => item !== ',' && item !== '%')
      .join('')
  )
}

/**
 * 保留指定小数位，不四舍五入
 * @param number
 * @param decimalPlaces
 */
export const truncateToDecimalPlaces = (number: number, decimalPlaces: number) => {
  const factor = Math.pow(10, decimalPlaces)
  // 先放大到指定小数位
  const tempNumber = Math.floor(number * factor)
  // 再缩小回来
  return tempNumber / factor
}

type FormatConfig = { range: [number, number], decimalPlaces: number }[]

/**
 * 按数值范围配置格式化保留指定小数位
 * @param value 数值
 * @param toThousandsFormat 是否格式化显示千分位
 * @param fractionDigits 指定保留小数位
 * @param formatConfig 自定义格式化数值范围配置
 */
export const formatDecimalPlaces = (
  value: number,
  toThousandsFormat = true,
  fractionDigits?: number,
  formatConfig?: FormatConfig
): string | number => {
  const _value = Math.abs(parseFloat(String(value)))

  // 非数字类型直接 return
  if (Number.isNaN(_value)) return value

  // 指定小数位直接格式化 return
  const formatValue = (num: number, decimalPlaces: number) => {
    const fmtNumber = num.toFixed(decimalPlaces)
    return `${value >= 0 ? (String(value).startsWith('+') ? '+' : '') : '-'}${toThousandsFormat ? thousandsFormat(fmtNumber, ',') : fmtNumber}`
  }

  if (fractionDigits !== undefined) {
    return formatValue(_value, Math.max(0, fractionDigits))
  }

  // 默认数值范围配置
  const defaultConfig = [
    /*{ range: [ 0, 1 ], decimalPlaces: 2 },
    { range: [ 1, 10 ], decimalPlaces: 1 },
    { range: [ 10, Infinity ], decimalPlaces: 1 },*/
    { range: [ 0, 0.1 ], decimalPlaces: 2 },
    { range: [ 0.1, Infinity ], decimalPlaces: 1 },
  ]
  // 优先应用外部数值范围配置
  const formattingConfig = formatConfig?.length ? formatConfig : defaultConfig

  // 按照数值范围格式化
  for (const config of formattingConfig) {
    if (_value >= config.range[0] && _value < config.range[1]) {
      return formatValue(_value, config.decimalPlaces)
    }
  }

  return _value
}

/**
 * 格式化百分比
 * @param value 当前值 12.3456 --> 12.34
 * @param fractionDigits
 * @returns
 */
export const formatRate = (value: number, fractionDigits = 1): string | number => {
  try {
    return value !== undefined && value !== null ? parseFloat(String(value)).toFixed(fractionDigits) : value
  } catch (e) {
    return value
  }
}

/**
 * 数值乘以100
 */
export const valueMultiplyByHundred = (value: any) => {
  return value !== undefined && value !== null ? multiplied(value, 100) : value
}

/**
 * 格式化百分比显示值（显示用）
 * @param value 当前值 0.123456 --> 12.34    (null --> -)
 * @returns
 */
export const formatRateDisplay = (value: any, useUnit = false): string | number => {
  const formatValue = value !== undefined && value !== null ? formatRate(valueMultiplyByHundred(value)) : '-'
  return useUnit ? `${formatValue}%` : formatValue
}

/**
 * 后端指标对应的key
 */
export enum IndicatorKeyEnum {
  '省份销售额-特殊指标' = 'ProvincePay',
  '品名销售额TOP' = 'PmSalesTop',
  '通用销售额TOP' = 'TymSalesTop',
  '品牌销售额TOP' = 'BrandSalesTop',
  '省区渗透率' = 'ProvincePermeability',
  '新老用户渗透率-卡片' = 'CardPermeability',
  '新老用户渗透率' = 'NewOldPermeability',
  '客单价' = 'ConsumerBill',
  '品单价' = 'AvgMedicinePay',
  '客品数' = 'ConsumptionSubsection',
  '订单量占比' = 'OrderProportion',
  '会员渗透率' = 'MemberPenetration',
  '年龄分布' = 'AgeCardProportion',
  '毛利率' = 'GrossMargin',
  '消费频率' = 'BuyFrequency',
  '销售额比例' = 'PayProportion',
  '铺货率' = 'Distribution',
  '动销率' = 'MovingRate',
  '终端机会分析' = 'TerminalOpportunity',
  '消费者机会分析' = 'ConsumerOpportunity',
  '店均销售额' = 'Pay',
  '店均订单量' = 'Order',
  '人均消费金额' = 'PerConsumption',
  '人均消费件数' = 'PerConsumptionNumber',
  // '空指标' = 'Nil',
  '客单毛利额' = 'GrossMarginConsumerBill',
  '店均毛利额' = 'StoreGrossMarginPay',
  '毛利额' = 'GrossMarginPay',
  '销售均价' = 'UnitPrice',
  '进货均价' = 'CostPay',
  '销售额权重' = 'PayWeight',
  '性别' = 'SexProportion',
  '新老用户比例' = 'NewOldProportion',
  '复购率' = 'FgRate',
  '可服用天数' = 'AvailableMedicationDay'
}

/**
 * 后端【会员指标】对应的key
 */
export enum MemberIndicatorKeyEnum {
  '会员渗透率',
  '毛利率',
  '铺货率',
  '动销率'
}


export type IndicatorKeyEnumTypes = keyof typeof IndicatorKeyEnum;

// 判断当前指标是不是会员指标
export const isMemberIndicator = (currentValue: string) => {
  return !!Object.keys(MemberIndicatorKeyEnum).find((key: string) => {
    return IndicatorKeyEnum[key as IndicatorKeyEnumTypes] === currentValue
  })
}

// 根据 后端指标对应的key 获取中文名
export const getIndicatorNameByVaule = (value: string) => {
  return Object.keys(IndicatorKeyEnum).find((key: string) => {
    return IndicatorKeyEnum[key as IndicatorKeyEnumTypes] === value
  })
}

/**
 * 指标类型枚举
 */
export enum IndicatorTypeEnum {
  '店均销售额' = 0,
  '销售额占比',
  '店均订单量',
  '订单量占比',
  '会员渗透率',
  '客单价',
  '品单价',
  '客品数',
  '购买频次',
  '毛利率',
  '客单毛利额',
  '店均毛利额',
  '铺货率',
  '动销率',
  '年龄段-人数占比',
  '关联购买-TGI',
  '性别比例',
  '进货均价',
  '销售均价',
  '复购率',
  '人均消费金额',
  '人均消费件数',
  '店均会员数变化',
  '会员销售额权重',
  '男女比', // 性别比例 衍生指标
  '会员数占比',
  '可服用天数'
}

/**
 * 指标枚举
 */
export enum IndicatorEnum {
  '数值' = 0,
  '对比期差值',
  '对比期增长',
  '权重',
  '增长指数'
}

/**
 * 指标单位类型枚举
 */
export enum IndicatorUnitTypeEnum {
  'scoreCard' = 0,
  'trendChart',
  'normal'
}

/**
 * 属于百分比、占比、率的指标清单，目前用于：
 * 1. 导出 Excel 做单元格格式化判断
 * 2. 增长驱动模型（树图）判断对比差值是否需要带单位
 */
export const isPercentageIndicatorList = [
  IndicatorTypeEnum['销售额占比'],
  IndicatorTypeEnum['订单量占比'],
  IndicatorTypeEnum['毛利率'],
  IndicatorTypeEnum['铺货率'],
  IndicatorTypeEnum['动销率'],
  IndicatorTypeEnum['性别比例'],
  IndicatorTypeEnum['年龄段-人数占比'],
  IndicatorTypeEnum['复购率'],
  IndicatorTypeEnum['会员渗透率'],
  IndicatorTypeEnum['店均会员数变化'],
  IndicatorTypeEnum['会员销售额权重'],
  IndicatorTypeEnum['会员数占比']
]

/**
 * 最大值是1的指标列表
 */
export const maxValueIsOneIndicatorList = [
  IndicatorTypeEnum['销售额占比'],
  IndicatorTypeEnum['订单量占比'],
  IndicatorTypeEnum['毛利率'],
  IndicatorTypeEnum['铺货率'],
  IndicatorTypeEnum['动销率'],
  IndicatorTypeEnum['复购率'],
  // 下面指标是求和为1
  // IndicatorTypeEnum['性别比例'],
  // IndicatorTypeEnum['年龄段-人数占比']
  // 新老客
]

/**
 * 求和的最大值是1的指标列表
 */
export const maxTotalValueIsOneIndicatorList = [
  IndicatorTypeEnum['性别比例'],
  IndicatorTypeEnum['年龄段-人数占比'],
  IndicatorTypeEnum['会员数占比'] // 也就是新老客
]

/**
 * 获取指标单位
 * @param indicatorType
 * @param indicator
 * @param indicatorUnitType
 * @returns
 */
export const getIndicatorUnit = (
  indicatorType: IndicatorTypeEnum,
  indicator: IndicatorEnum,
  indicatorUnitType: IndicatorUnitTypeEnum
): string => {
  // console.log(indicatorType,indicator)
  const unitMap: anyObj = {
    [IndicatorTypeEnum['店均销售额']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/店/月',
        [IndicatorUnitTypeEnum['trendChart']]: '元/店/月',
        [IndicatorUnitTypeEnum['normal']]: '元/店/月'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/店/月',
        [IndicatorUnitTypeEnum['trendChart']]: '元/店/月',
        [IndicatorUnitTypeEnum['normal']]: '元/店/月'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['权重']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['销售额占比']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['店均订单量']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '单/店/月',
        [IndicatorUnitTypeEnum['trendChart']]: '单/店/月',
        [IndicatorUnitTypeEnum['normal']]: '单/店/月'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '单/店/月',
        [IndicatorUnitTypeEnum['trendChart']]: '单/店/月',
        [IndicatorUnitTypeEnum['normal']]: '单/店/月'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['订单量占比']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['会员数占比']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['会员渗透率']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['客单价']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/单',
        [IndicatorUnitTypeEnum['trendChart']]: '元/单',
        [IndicatorUnitTypeEnum['normal']]: '元/单'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/单',
        [IndicatorUnitTypeEnum['trendChart']]: '元/单',
        [IndicatorUnitTypeEnum['normal']]: '元/单'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['品单价']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/件',
        [IndicatorUnitTypeEnum['trendChart']]: '元/件',
        [IndicatorUnitTypeEnum['normal']]: '元/件'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/件',
        [IndicatorUnitTypeEnum['trendChart']]: '元/件',
        [IndicatorUnitTypeEnum['normal']]: '元/件'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['客品数']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '件/单',
        [IndicatorUnitTypeEnum['trendChart']]: '件/单',
        [IndicatorUnitTypeEnum['normal']]: '件/单'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '件/单',
        [IndicatorUnitTypeEnum['trendChart']]: '件/单',
        [IndicatorUnitTypeEnum['normal']]: '件/单'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['毛利率']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['客单毛利额']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/单',
        [IndicatorUnitTypeEnum['trendChart']]: '元/单',
        [IndicatorUnitTypeEnum['normal']]: '元/单'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/单',
        [IndicatorUnitTypeEnum['trendChart']]: '元/单',
        [IndicatorUnitTypeEnum['normal']]: '元/单'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['店均毛利额']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/店/月',
        [IndicatorUnitTypeEnum['trendChart']]: '元/店/月',
        [IndicatorUnitTypeEnum['normal']]: '元/店/月'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/店/月',
        [IndicatorUnitTypeEnum['trendChart']]: '元/店/月',
        [IndicatorUnitTypeEnum['normal']]: '元/店/月'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['铺货率']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['动销率']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['购买频次']]: {
      // V0.1.0 当前计算逻辑非月均的概念，故单位应该是“XX次”，而非“XX次/月”
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '次',
        [IndicatorUnitTypeEnum['trendChart']]: '次',
        [IndicatorUnitTypeEnum['normal']]: '次'
      },
      // V0.1.0 当前计算逻辑非月均的概念，故单位应该是“XX次”，而非“XX次/月”
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '次',
        [IndicatorUnitTypeEnum['trendChart']]: '次',
        [IndicatorUnitTypeEnum['normal']]: '次'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['年龄段-人数占比']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['关联购买-TGI']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['性别比例']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['男女比']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['进货均价']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/件',
        [IndicatorUnitTypeEnum['trendChart']]: '元/件',
        [IndicatorUnitTypeEnum['normal']]: '元/件'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/件',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: '元/件'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['销售均价']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/件',
        [IndicatorUnitTypeEnum['trendChart']]: '元/件',
        [IndicatorUnitTypeEnum['normal']]: '元/件'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元/件',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: '元/件'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['复购率']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      }
    },
    [IndicatorTypeEnum['人均消费金额']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元',
        [IndicatorUnitTypeEnum['trendChart']]: '元',
        [IndicatorUnitTypeEnum['normal']]: '元'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '元',
        [IndicatorUnitTypeEnum['trendChart']]: '元',
        [IndicatorUnitTypeEnum['normal']]: '元'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['人均消费件数']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '件',
        [IndicatorUnitTypeEnum['trendChart']]: '件',
        [IndicatorUnitTypeEnum['normal']]: '件'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '件',
        [IndicatorUnitTypeEnum['trendChart']]: '件',
        [IndicatorUnitTypeEnum['normal']]: '件'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['增长指数']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '',
        [IndicatorUnitTypeEnum['trendChart']]: '',
        [IndicatorUnitTypeEnum['normal']]: ''
      }
    },
    [IndicatorTypeEnum['店均会员数变化']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
    },
    [IndicatorTypeEnum['会员销售额权重']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
      [IndicatorEnum['对比期增长']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '%',
        [IndicatorUnitTypeEnum['trendChart']]: '%',
        [IndicatorUnitTypeEnum['normal']]: '%'
      },
    },
    [IndicatorTypeEnum['可服用天数']]: {
      [IndicatorEnum['数值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '天/人',
        [IndicatorUnitTypeEnum['trendChart']]: '天/人',
        [IndicatorUnitTypeEnum['normal']]: '天/人'
      },
      [IndicatorEnum['对比期差值']]: {
        [IndicatorUnitTypeEnum['scoreCard']]: '天/人',
        [IndicatorUnitTypeEnum['trendChart']]: '天/人',
        [IndicatorUnitTypeEnum['normal']]: '天/人'
      },
    }
  }

  if (import.meta.env.DEV && unitMap[indicatorType]?.[indicator]?.[indicatorUnitType] === undefined) {
    console.warn('getIndicatorUnit---暂无单位')
  }

  return unitMap[indicatorType]?.[indicator]?.[indicatorUnitType] || ''
}

/**
 *  指标通用数值格式化
 * @param value 数值
 * @param emptyValue 缺失数据
 * @param fractionDigits 保留小数位
 * @returns
 */
const indicatorCommonValueFormatter = (
  value: any,
  emptyValue: any,
  fractionDigits?: number
): {
  value: any
  isEmpty: boolean
} => {
  if (value === undefined || value === null) {
    return {
      value: emptyValue,
      isEmpty: true
    }
  }

  return {
    value: formatDecimalPlaces(Number(value), true, fractionDigits), // Number(value).toFixed(fractionDigits || 2),
    isEmpty: false
  }
}

/**
 * 指标通用占比格式化
 * @param value 数值
 * @param emptyValue 缺失数据
 * @param fractionDigits 保留小数位
 * @returns
 */
export const indicatorCommonRateFormatter = (
  value: any,
  emptyValue: any,
  fractionDigits?: number
): {
  value: any
  isEmpty: boolean
} => {
  if (value === undefined || value === null) {
    return {
      value: emptyValue,
      isEmpty: true
    }
  }

  return {
    value: formatDecimalPlaces(multiplied(Number(value), 100), true, fractionDigits), //  Number((value * 100)).toFixed(fractionDigits || 2),
    isEmpty: false
  }
}

/**
 * 指标通用增长指数格式化
 * @param value 数值
 * @param emptyValue 缺失数据
 * @param fractionDigits 保留小数位
 * @returns
 */
const indicatorCommonGrowthIndexFormatter = (
  value: any,
  emptyValue: any,
  fractionDigits?: number
): {
  value: any
  isEmpty: boolean
} => {
  if (value === undefined || value === null) {
    return {
      value: emptyValue,
      isEmpty: true
    }
  }

  return {
    value: formatDecimalPlaces(multiplied(Number(value), 100), true, fractionDigits || 0), //  Number((value * 100)).toFixed(fractionDigits || 2),
    isEmpty: false
  }
}

/**
 * 店均销售额 数值格式化
 * @param value 数值
 * @param emptyValue 缺失数据
 * @param fractionDigits 保留小数位
 */
const indicatorPayValueFormatter = (
  value: any,
  emptyValue: any,
  fractionDigits?: number
): {
  value: any
  isEmpty: boolean
} => {
  if (value === undefined || value === null) {
    return {
      value: emptyValue,
      isEmpty: true
    }
  }

  const config: FormatConfig = [
    { range: [ 0, 0.1 ], decimalPlaces: 2 },
    { range: [ 0.1, 100 ], decimalPlaces: 1 },
    { range: [ 100, Infinity ], decimalPlaces: 0 },
  ]

  return {
    value: formatDecimalPlaces(Number(value), true, fractionDigits, config), // Number(value).toFixed(fractionDigits || 2),
    isEmpty: false
  }
}

/**
 * 关联购买-TGI 数值格式化
 * @param value 数值
 * @param emptyValue 缺失数据
 * @param fractionDigits 保留小数位
 */
const indicatorTgiValueFormatter = (
  value: any,
  emptyValue: any,
  fractionDigits?: number
): {
  value: any
  isEmpty: boolean
} => {
  if (value === undefined || value === null) {
    return {
      value: emptyValue,
      isEmpty: true
    }
  }

  /*const config: FormatConfig = [
    { range: [ 0, 1 ], decimalPlaces: 1 },
    { range: [ 1, 10 ], decimalPlaces: 1 },
    { range: [ 10, 1000 ], decimalPlaces: 1 },
    { range: [ 1000, Infinity ], decimalPlaces: 0 },
  ]*/

  const config: FormatConfig = [
    { range: [ 0, Infinity ], decimalPlaces: 0 }
  ]

  return {
    value: formatDecimalPlaces(Number(value), true, fractionDigits, config), // Number(value).toFixed(fractionDigits || 2),
    isEmpty: false
  }
}

/**
 * 店均会员变化率 数值格式化
 * @param value 数值
 * @param emptyValue 缺失数据
 * @param fractionDigits 保留小数位
 */
const indicatorStoreMemberValueFormatter = (
  value: any,
  emptyValue: any,
  fractionDigits?: number
): {
  value: any
  isEmpty: boolean
} => {
  if (value === undefined || value === null) {
    return {
      value: emptyValue,
      isEmpty: true
    }
  }

  return {
    value: formatDecimalPlaces(Number(value), true, fractionDigits),
    isEmpty: false
  }
}

/**
 * 性别比例-男女比 数值格式化
 * @param value 数值
 * @param emptyValue 缺失数据
 * @param fractionDigits 保留小数位
 */
const indicatorSexRatioRateFormatter = (
  value: any,
  emptyValue: any,
  fractionDigits?: number
): {
  value: any
  isEmpty: boolean
} => {
  if (value === undefined || value === null) {
    return {
      value: emptyValue,
      isEmpty: true
    }
  }

  const config: FormatConfig = [
    { range: [ 0, Infinity ], decimalPlaces: 0 }
  ]

  return {
    value: formatDecimalPlaces(multiplied(Number(value), 100), true, fractionDigits, config),
    isEmpty: false
  }
}

/**
 * 获取指标格式化函数
 * @param indicatorType
 * @param indicator
 * @param fractionDigits
 */
export const getIndicatorFormatter = (
  indicatorType: IndicatorTypeEnum,
  indicator: IndicatorEnum,
  fractionDigits?: number
): Fn => {
  const defaultFormatter = (value: any) => {
    if (import.meta.env.DEV) {
      console.warn('getIndicatorFormatter---暂无格式化函数')
    }
    return {
      value,
      isEmpty: value === undefined || value === null
    }
  }

  const formatterMap: anyObj = {
    [IndicatorTypeEnum['店均销售额']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorPayValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['权重']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['销售额占比']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['店均订单量']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['订单量占比']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['会员数占比']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['会员渗透率']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['客单价']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['品单价']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['客品数']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['购买频次']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['毛利率']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['客单毛利额']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['店均毛利额']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['铺货率']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['动销率']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['年龄段-人数占比']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: defaultFormatter,
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['关联购买-TGI']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorTgiValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: defaultFormatter,
      [IndicatorEnum['对比期增长']]: defaultFormatter,
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['性别比例']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['男女比']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorSexRatioRateFormatter(value, '-', fractionDigits)
    },
    [IndicatorTypeEnum['进货均价']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['销售均价']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['复购率']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits)
    },
    [IndicatorTypeEnum['人均消费金额']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['人均消费件数']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['增长指数']]: (value: any) => indicatorCommonGrowthIndexFormatter(value, '-')
    },
    [IndicatorTypeEnum['店均会员数变化']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorStoreMemberValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorStoreMemberValueFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
    },
    [IndicatorTypeEnum['会员销售额权重']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
      [IndicatorEnum['对比期增长']]: (value: any) => indicatorCommonRateFormatter(value, '-', fractionDigits),
    },
    [IndicatorTypeEnum['可服用天数']]: {
      [IndicatorEnum['数值']]: (value: any) => indicatorCommonValueFormatter(value, 0, fractionDigits),
      [IndicatorEnum['对比期差值']]: (value: any) => indicatorCommonValueFormatter(value, '-', fractionDigits),
    }
  }

  return formatterMap[indicatorType]?.[indicator] || defaultFormatter
}

/**
 * 获取指标格式化
 * @param indicatorType 指标类型 （店均销售额...）
 * @param indicator 指标 （数值、对比期差值...）
 * @param indicatorUnitType 指标单位类型 （scoreCard...）
 * @param fractionDigits 保留小数位 不传就是默认全局格式化类型
 * @returns
 */
export const getIndicator = ({
  indicatorType,
  indicator,
  indicatorUnitType,
  fractionDigits
}: {
  indicatorType: IndicatorTypeEnum
  indicator: IndicatorEnum
  indicatorUnitType: IndicatorUnitTypeEnum
  fractionDigits?: number
}): {
  unit: string
  formatter: Fn
} => {
  const unit = getIndicatorUnit(indicatorType, indicator, indicatorUnitType)
  const formatter = getIndicatorFormatter(indicatorType, indicator, fractionDigits)
  return {
    unit: unit,
    formatter: formatter
  }
}

/**
 * 排名依据
 */
export const rankBasisList = [
  {
    key: IndicatorKeyEnum['销售额比例'],
    name: '销售额占比'
  },
  {
    key: IndicatorKeyEnum['订单量占比'],
    name: '订单量占比'
  },
  {
    key: IndicatorKeyEnum['会员渗透率'],
    name: '渗透率（会员）'
  },
]

/**
 * 展示指标
 */
export const displayIndicatorsList = [
  {
    key: IndicatorKeyEnum['销售额比例'],
    name: '销售额占比',
    unit: ''
  },
  {
    key: IndicatorKeyEnum['订单量占比'],
    name: '订单量占比',
    unit: ''
  },
  {
    key: IndicatorKeyEnum['店均销售额'],
    name: '店均销售额',
    unit: getIndicatorUnit(IndicatorTypeEnum['店均销售额'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['店均订单量'],
    name: '店均订单量',
    unit: getIndicatorUnit(IndicatorTypeEnum['店均订单量'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['人均消费金额'],
    name: '人均消费金额',
    unit: getIndicatorUnit(IndicatorTypeEnum['人均消费金额'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['人均消费件数'],
    name: '人均消费件数',
    unit: getIndicatorUnit(IndicatorTypeEnum['人均消费件数'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['客单价'],
    name: '平均客单价',
    unit: getIndicatorUnit(IndicatorTypeEnum['客单价'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  }
]

/**
 * 展示指标（品牌分析）
 */
export const displayIndicatorsListOfBrandAnalysis = [
  {
    key: IndicatorKeyEnum['销售额比例'],
    name: '销售额占比',
    unit: ''
  },
  {
    key: IndicatorKeyEnum['订单量占比'],
    name: '订单量占比',
    unit: ''
  },
  {
    key: IndicatorKeyEnum['店均销售额'],
    name: '店均销售额',
    unit: getIndicatorUnit(IndicatorTypeEnum['店均销售额'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['店均订单量'],
    name: '店均订单量',
    unit: getIndicatorUnit(IndicatorTypeEnum['店均订单量'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['会员渗透率'],
    name: '渗透率（会员小票）',
    unit: getIndicatorUnit(IndicatorTypeEnum['会员渗透率'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['客单价'],
    name: '客单价',
    unit: getIndicatorUnit(IndicatorTypeEnum['客单价'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['品单价'],
    name: '品单价',
    unit: getIndicatorUnit(IndicatorTypeEnum['品单价'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['客品数'],
    name: '客品数',
    unit: getIndicatorUnit(IndicatorTypeEnum['客品数'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['消费频率'],
    name: '购药频次',
    unit: getIndicatorUnit(IndicatorTypeEnum['购买频次'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['毛利率'],
    name: '毛利率（POS小票）',
    unit: getIndicatorUnit(IndicatorTypeEnum['毛利率'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['铺货率'],
    name: '铺货率',
    unit: getIndicatorUnit(IndicatorTypeEnum['铺货率'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  },
  {
    key: IndicatorKeyEnum['动销率'],
    name: '动销率',
    unit: getIndicatorUnit(IndicatorTypeEnum['动销率'], IndicatorEnum['数值'], IndicatorUnitTypeEnum['trendChart'])
  }
]

/**
 * 获取求和的最大值是1的指标yAxisDisplayMaxValue
 * @param indicatorType
 * @returns
 */
export const getMaxTotalValueIsOneIndicatorYAxisDisplayMaxValue = (indicatorType: IndicatorTypeEnum) => {
  if (!maxTotalValueIsOneIndicatorList.includes(indicatorType)) {
    return Infinity
  }

  return 1
}
